import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AxiosError } from 'axios';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import { EDevice, URL_ROUTES } from '@/constants';
import { usePaymentError } from '@/modules/perseo-billing/hooks/use-payment-error';
import {
  buildPurchaseSearchParams,
  detectDevice
} from '@/modules/perseo-billing/hooks/utils';
import { EPayStatus } from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import type { IOrderIntentStateResponse } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/orderV2';
import { billingQueryKeys } from '@/modules/perseo-billing/services/api/billing/queries';
import { getBillingOrderStatus } from '@/modules/perseo-billing/services/api/billing/queries/index';
import {
  setCencoPayStatus,
  setIsModalQRCencopayVisible
} from '@/modules/perseo-billing/store/slices/billing/index';
import type { IErrorResponse } from '@/modules/perseo-billing/utils/error-handler/index';
import {
  isDeviceAndroid,
  isDeviceIos,
  useNavigation
} from '@/modules/perseo-billing/utils/index';

const useEnableRequest = () => {
  const billing = useAppSelector((state) => state.billing);
  const { cencoPayQR } = billing;
  const orderNumber = cencoPayQR?.orderNumber ?? '';

  const [isRequestEnabled, setIsRequestEnabled] = useState(false);

  const {
    isModalQRCencopayVisible,
    isModalCencopayGetTheAppVisible,
    cencoPayStatus,
    isCencoPayTimeOut
  } = useAppSelector((state) => state?.billing) || {};

  const hasOrder = !!orderNumber;
  const isConfirmed = cencoPayStatus === EPayStatus.CONFIRMED;
  const isRejected =
    cencoPayStatus === EPayStatus.REJECTED ||
    cencoPayStatus === EPayStatus.CANCELLED;
  const isNotOnFinalStep = !isConfirmed && !isRejected;

  const device = detectDevice();
  const isMobile = device === EDevice.ANDROID || device === EDevice.IOS;

  const isWebView = isDeviceAndroid() || isDeviceIos();
  const validations = hasOrder && isNotOnFinalStep && !isCencoPayTimeOut;
  const defaultCondition = validations && !isWebView;

  const webViewValidations =
    (isWebView && validations) || isModalCencopayGetTheAppVisible;

  useEffect(() => {
    if (isMobile) {
      setIsRequestEnabled(defaultCondition || webViewValidations);
    } else {
      setIsRequestEnabled(defaultCondition && isModalQRCencopayVisible);
    }
  }, [
    defaultCondition,
    isMobile,
    isModalQRCencopayVisible,
    webViewValidations
  ]);

  return isRequestEnabled;
};

export const useGetQRValidate = () => {
  const [searchParams] = useSearchParams();
  const urlCencopayOrderNumber = searchParams.get('cencopayOrderId');
  const billing = useAppSelector((state) => state.billing);
  const { cencoPayQR } = billing || {};
  const orderNumber = (cencoPayQR?.orderNumber || urlCencopayOrderNumber) ?? '';
  const navigate = useNavigate();

  const { handlePaymentError } = usePaymentError();
  const { removeQueryString } = useNavigation();

  const enabled = useEnableRequest();

  const dispatch = useAppDispatch();
  const queryResult = useQuery<IOrderIntentStateResponse>(
    billingQueryKeys.qrValidatedModal,
    () => getBillingOrderStatus({ orderNumber }),
    {
      enabled: !!urlCencopayOrderNumber || enabled,
      refetchInterval: 5000,
      refetchIntervalInBackground: true,
      retry(failureCount: number, error) {
        const axiosError = error as AxiosError;
        return axiosError.status === 400 && failureCount < 5;
      },
      onSuccess(data) {
        const status = data.state;
        dispatch(setCencoPayStatus(status || EPayStatus.PENDING));
        const isConfirmed = status === EPayStatus.CONFIRMED;

        const orderId = data?.orderId || urlCencopayOrderNumber || '';
        const urlSearchParams = buildPurchaseSearchParams(orderId);
        const paramsString = urlSearchParams.toString();

        if (isConfirmed) {
          const orderProcessingUrl = `${URL_ROUTES.BILLING_ORDER_PROCESSING_SUCCESS_PATH}?${paramsString}`;
          navigate(orderProcessingUrl, { replace: true });
        }

        const isErrorCase =
          status === EPayStatus.REJECTED || status === EPayStatus.CANCELLED;

        if (isErrorCase) {
          dispatch(setIsModalQRCencopayVisible(false));
          const error = {
            error: {
              meta: {
                errorCode: status
              }
            }
          };
          handlePaymentError(error);
          removeQueryString(['cencopayOrderId']);
        }
      },
      onError(err: unknown) {
        const error = err as AxiosError<IErrorResponse>;
        handlePaymentError(error?.response?.data);
      }
    }
  );
  return {
    ...queryResult
  };
};
